// Custom hook for SafeHaven Scorecard business logic
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { api_url } from "@/utils/apiConfig";
import {
  generateValuesSPX,
  catListDropdown,
  buildMergedAssetOptions,
  findCustomAsset,
  getAssetTypeCode,
} from "@/utils/helper";
import {
  OtherDropdown,
  generateMix,
  generateMixReturnsWealth,
  binMapping,
  generateCartoonsReturns,
} from "@/utils/helper";
import useCustomAssets from "@/hooks/useCustomAssets";

import {
  INSURANCE_TYPES,
  INITIAL_DATA_BAR,
  INITIAL_REGIME_BAR,
} from "../utils/constants";

import {
  avgCal,
  maxCalc,
  minCalc,
  avgCalc,
  avgCalculate,
  shuffleMatrixRows,
  fillNewMatrix,
  filterRegimeYears,
  createBinMatrix,
  calReturn,
  calYearMatrix,
  calculatePercentilePaths,
  calculateWealthMatrixForOneTwenty,
  lastColumnValue,
  amDataCal,
  ddDataCal,
  cagrCal,
  calAmGm,
  calReshuffledBin,
  generatePNLValues,
} from "../utils/calculations";
import { filterHistoricalAssetsByDateRange } from "@/utils/userAsset";
import {
  callSafeHavenScorecard,
  buildScorecardRequest,
} from "@/services/api/safeHavenScorecard";
import {
  applyScorecardResponse,
  resolveCartoonConfig,
} from "@/services/api/applyScorecardResponse";
// SCORECARD_PARITY_TESTKIT — Mulberry32(PARITY_SEED) for JS Monte Carlo
import {
  scorecardRandom,
  resetParityRng,
  PARITY_SEED,
} from "@/dev/scorecardParityTestkit"; // SCORECARD_PARITY_TESTKIT
import { useScorecardInputs } from "@/hooks/useScorecardInputs";
import {
  portfolioRequestFailure,
  portfolioRequestStart,
  portfolioRequestSuccess,
} from "@/store/slices/portfolioSlice";

const selectedValueFlip = 25;
const selectedValuePath = 1000;
const timeFrame = "a";
const useScorecard = ({ usePyScorecard = false } = {}) => {
  const dispatch = useDispatch();
  const {
    portfolio,
    patch,
    setFromYear,
    setToYear,
    setYearsList,
    setSelectedType,
    setInsuranceSliderValue,
    setLock,
    setRegimeCount,
    setRegimeSelectors,
    setAlloc,
    setPercentileDropdown,
  } = useScorecardInputs();

  const {
    fromYear,
    toYear,
    yearsList,
    selectedType,
    firstAsset,
    firstAssetName,
    secondAsset,
    secondAssetName,
    insuranceSliderValue,
    lock,
    regimeCount,
    regimeSelectors,
    alloc,
    percentileDropdown,
  } = portfolio;

  const [firstAssetSeries, setFirstAssetSeries] = useState({
    firstAssetValues: [],
    firstAssetReturns: [],
  });
  const { firstAssetValues, firstAssetReturns } = firstAssetSeries;

  const setFirstAssetData = useCallback(
    (updater) => {
      setFirstAssetSeries((series) => {
        const prev = {
          firstAsset,
          firstAssetName,
          firstAssetValues: series.firstAssetValues,
          firstAssetReturns: series.firstAssetReturns,
        };
        const next =
          typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
        if (
          next.firstAsset !== firstAsset ||
          next.firstAssetName !== firstAssetName
        ) {
          patch({
            firstAsset: next.firstAsset,
            firstAssetName: next.firstAssetName,
          });
        }
        return {
          firstAssetValues: next.firstAssetValues ?? [],
          firstAssetReturns: next.firstAssetReturns ?? [],
        };
      });
    },
    [firstAsset, firstAssetName, patch]
  );

  const [minMaxAvgArray, setMinMaxAvgArray] = useState({
    minArray: [],
    maxArray: [],
    avgArray: [],
  });
  const [minMaxAvgArrayNoCrash, setMinMaxAvgArrayNoCrash] = useState({
    minArrayNocrash: [],
    maxArrayNocrash: [],
    averageArrayNocrash: [],
  });
  const [minMaxAvgAssetArray, setMinMaxAvgAssetArray] = useState({
    minAssetArray: [],
    maxAssetArray: [],
    averageAssetArray: [],
  });
  const [minMaxAvgAssetArrayNocrash, setMinMaxAvgAssetArrayNocrash] = useState({
    minAssetArrayNocrash: [],
    maxAssetArrayNocrash: [],
    averageAssetArrayNocrash: [],
  });
  const [minMaxAvgAssetReshuffleArray, setMinMaxAvgAssetReshuffleArray] =
    useState({
      minAssetReshuffleArray: [],
      maxAssetReshuffleArray: [],
      averageAssetReshuffleArray: [],
    });

  const [amValues, setAmValues] = useState({
    am: 0,
    amNocrash: 0,
    amMean: 0,
    amMeanNocrash: 0,
    amMeanReshuffle: 0,
  });
  const [gmValues, setGmValues] = useState({ gm: 0, gmNocrash: 0 });
  const [yearMatrixData, setYearMatrixData] = useState([]);
  const [yearMatrixDataNocrash, setYearMatrixDataNocrash] = useState([]);

  // Other Asset - merge custom user assets with static options
  const userAssets = useCustomAssets();
  const { user } = useSelector((state) => state.userDetails);
  const scorecardUserId = user?.id != null ? Number(user.id) : undefined;
  
  // Filter historical assets so only those with full data coverage are used
  const filteredUserAssets = useMemo(() => {
    return filterHistoricalAssetsByDateRange(userAssets, fromYear, toYear, "monthly");
  }, [userAssets, fromYear, toYear]);

  const firstAssetDropdown = useMemo(
    () => buildMergedAssetOptions(catListDropdown, filteredUserAssets, true),
    [filteredUserAssets]
  );
  const secondAssetDropdown = useMemo(
    () => buildMergedAssetOptions(OtherDropdown, filteredUserAssets, true),
    [filteredUserAssets]
  );

  const [secondAssetSeries, setSecondAssetSeries] = useState({
    secondAssetValues: [],
    secondAssetReturns: [],
  });
  const { secondAssetValues, secondAssetReturns } = secondAssetSeries;

  const setSecondAssetData = useCallback(
    (updater) => {
      setSecondAssetSeries((series) => {
        const prev = {
          secondAsset,
          secondAssetName,
          secondAssetValues: series.secondAssetValues,
          secondAssetReturns: series.secondAssetReturns,
        };
        const next =
          typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
        if (
          next.secondAsset !== secondAsset ||
          next.secondAssetName !== secondAssetName
        ) {
          patch({
            secondAsset: next.secondAsset,
            secondAssetName: next.secondAssetName,
          });
        }
        return {
          secondAssetValues: next.secondAssetValues ?? [],
          secondAssetReturns: next.secondAssetReturns ?? [],
        };
      });
    },
    [secondAsset, secondAssetName, patch]
  );

  const isInsurance = useMemo(() => {
    return INSURANCE_TYPES.some((type) => secondAsset.includes(type));
  }, [secondAsset]);

  //cpi
  const [cpiValues, setCpiValues] = useState([]);
  const [percentile2DataMaxReturnAllocation, setPercentile2DataMaxAllocation] =
    useState(0);
  const allocCEAplotRef = useRef(null);

  const [dataBar, setDataBar] = useState(INITIAL_DATA_BAR);

  const [regimeBar, setRegimeBar] = useState(INITIAL_REGIME_BAR);
  const [regimeBarForNocrash, setRegimeBarForNocrash] =
    useState(INITIAL_REGIME_BAR);

  const [mixAssetReshuffleArray, setMixAssetReshuffleArray] = useState({
    mixMinAssetReshuffleArray: [],
    mixMaxAssetReshuffleArray: [],
    mixAvgAssetReshuffleArray: [],
  });
  const [mixAssetArray, setMixAssetArray] = useState({
    mixMinAssetArray: [],
    mixMaxAssetArray: [],
    mixAvgAssetArray: [],
  });
  const [mixAmGm, setMixAmGm] = useState({
    mixAM: [],
    mixGM: [],
  });
  const [mixAmGmReshuffle, setMixAmGmReshuffle] = useState({
    mixAMReshuffle: [],
    mixGMReshuffle: [],
  });

  //for normal or default
  const [baseAssetMatrixRetruns, setBaseAssetMatrixReturns] = useState([]);
  const [otherAssetMatrixRetruns, setOtherAssetMatrixReturns] = useState([]);
  //for reshuffle
  const [
    otherAssetMatrixReshuffleReturns,
    setOtherAssetMatrixReshuffleReturns,
  ] = useState([]);

  //for nocrash
  const [baseAssetMatrixReturnsNoCrash, setBaseAssetMatrixReturnsNoCrash] =
    useState([]);
  const [otherAssetMatrixReturnsNoCrash, setOtherAssetMatrixReturnsNoCrash] =
    useState([]);

  const [minMaxAvgMixNoCrash, setMinMaxAvgMixNoCrash] = useState({
    minMixNoCrash: [],
    maxMixNoCrash: [],
    avgMixNoCrash: [],
  });
  const [amGmMixNoCrash, setAmGmMixNoCrash] = useState({
    amMixNoCrash: [],
    gmMixNoCrash: [],
  });

  //loader
  const [loading, setLoading] = useState(true);

  // Precomputed Chart5 curves from Python API (Phase A)
  const [chart5ByMode, setChart5ByMode] = useState({
    normal: null,
    reshuffle: null,
    nocrash: null,
  });

  //for cartoons generation
  const [bins, setBins] = useState([]);

  // Custom/historical overrides for Python API path (JS Monte Carlo / local UI only)
  const [regimeCommitNonce, setRegimeCommitNonce] = useState(0);
  const [allocCommitNonce, setAllocCommitNonce] = useState(0);
  const [unlockNonce, setUnlockNonce] = useState(0);
  const lastAllocCommitNonceRef = useRef(allocCommitNonce);
  const allocRef = useRef(alloc);
  allocRef.current = alloc;
  const lockRef = useRef(lock);
  lockRef.current = lock;

  const buildDefaultRegimeSelectors = useCallback((dataLength, count) => {
    if (dataLength < 2) return [1, dataLength || 1];
    const step = Math.round(dataLength / (count + 1));
    const buffer = [1];
    for (let i = 1; i <= count; i++) buffer.push(step * i);
    buffer.push(dataLength);
    return buffer;
  }, []);

  // API call function
  const callAPI = useCallback(
    async (options, responseSetter, returnsSetter) => {
      try {
        setLoading(true);
        const data = await axios.post(`${api_url}/fetch-data`, options);
        let raw_data = (data && data.data.data) || [];
        responseSetter(raw_data);
        let returns = generateValuesSPX(
          options.fromYear || fromYear,
          options.toYear || toYear,
          raw_data
        );
        returnsSetter(returns);
        setLoading(false);
        return true;
      } catch (error) {
        setLoading(false);
      }
    },
    [fromYear, toYear]
  );

  const normalizeAnnualReturns = useCallback((annualReturns) => {
    if (!annualReturns) return [];

    if (Array.isArray(annualReturns)) {
      return annualReturns
        .map((item) => {
          if (Array.isArray(item) && item.length >= 2) {
            return [Number(item[0]), Number(item[1])];
          }
          if (item && typeof item === "object") {
            const year = Number(item.year ?? item.Year ?? item.y);
            const value = Number(
              item.annual_ret ??
              item.annualRet ??
              item.return ??
              item.returns ??
              item.value ??
              item.ret
            );
            return Number.isFinite(year) && Number.isFinite(value)
              ? [year, value]
              : null;
          }
          return null;
        })
        .filter(Boolean)
        .sort((a, b) => a[0] - b[0]);
    }

    if (typeof annualReturns === "object") {
      return Object.entries(annualReturns)
        .map(([year, value]) => [Number(year), Number(value)])
        .filter(([year, value]) => Number.isFinite(year) && Number.isFinite(value))
        .sort((a, b) => a[0] - b[0]);
    }

    return [];
  }, []);

  const resolveAssetSource = useCallback(
    (assetValue) => {
      const customAsset = findCustomAsset(assetValue, userAssets);
      if (!customAsset) {
        return {
          assetKind: "standard",
          apiType: assetValue,
          annualReturns: [],
        };
      }

      const baseAsset =
        customAsset.baseAsset ||
        customAsset.base_asset ||
        customAsset.baseasset ||
        customAsset.base ||
        customAsset.underlyingAsset ||
        customAsset.underlying_asset ||
        customAsset.benchmark ||
        "";
      const mappedBaseAssetType = getAssetTypeCode(baseAsset);

      const annualReturns = normalizeAnnualReturns(
        customAsset.annual_ret ||
        customAsset.annualRet ||
        customAsset.annual_returns ||
        customAsset.annualReturns
      );

      const assetType = String(
        customAsset.assetType ||
        customAsset.type ||
        customAsset.sourceType ||
        customAsset.asset_source ||
        ""
      ).toLowerCase();

      const isHistorical =
        assetType.includes("hist") || (annualReturns.length > 0 && !baseAsset);

      if (isHistorical) {
        return {
          assetKind: "historical",
          apiType: null,
          annualReturns,
        };
      }

      return {
        assetKind: "custom",
        apiType: mappedBaseAssetType || baseAsset || assetValue,
        annualReturns,
      };
    },
    [userAssets, normalizeAnnualReturns]
  );

  // Asset handlers
  const handleFirstAsset = useCallback(
    async (value, text) => {
      setFirstAssetData((p) => ({
        ...p,
        firstAsset: value,
        firstAssetName: text,
      }));

      const { assetKind, apiType, annualReturns } = resolveAssetSource(value);
      if (assetKind === "historical") {
        const annualReturnSeries = generateValuesSPX(fromYear, toYear, annualReturns);
        setFirstAssetData((p) => ({
          ...p,
          firstAssetValues: annualReturns,
          firstAssetReturns: annualReturnSeries,
        }));
        return;
      }
      if (usePyScorecard) return;
      await callAPI(
        { fromYear: 1901, toYear: 2025, type: apiType, frequency: timeFrame },
        (res) => setFirstAssetData((p) => ({ ...p, firstAssetValues: res })),
        (res) => setFirstAssetData((p) => ({ ...p, firstAssetReturns: res }))
      );
    },
    [callAPI, timeFrame, resolveAssetSource, fromYear, toYear, usePyScorecard]
  );

  const handleSecondAsset = useCallback(
    async (value, text) => {
      setSecondAssetData((p) => ({
        ...p,
        secondAsset: value,
        secondAssetName: text,
      }));

      const cartoonCases = {
        "Store-of-Value [CPI+2%]": ["Store-of-Value CPI", [2, 2, 2, 2]],
        "Store-of-Value [7%]": ["Store-of-Value", [7, 7, 7, 7]],
        "Alpha [20,7.5,5,5]": ["Alpha", [20, 7.5, 5, 5]],
        "Alpha [15,10,2.5,2.5]": ["Alpha", [15, 10, 2.5, 2.5]],
        "INSUR[10-to-1]": ["Insurance", [1000, -100, -100, -100]],
        "INSUR[12-to-1]": ["Insurance", [1200, -100, -100, -100]],
        "INSUR[15-to-1]": ["Insurance", [1500, -100, -100, -100]],
      };

      if (cartoonCases[value]) {
        if (usePyScorecard) {
          return;
        }
        const [type, values] = cartoonCases[value];
        setSecondAssetData((p) => ({
          ...p,
          secondAssetValues: [
            ...generateCartoonsReturns(
              fromYear,
              toYear,
              type,
              values,
              cpiValues,
              firstAssetValues,
              bins
            ),
          ],
        }));
      } else {
        const { assetKind, apiType, annualReturns } = resolveAssetSource(value);

        if (assetKind === "historical") {
          const annualReturnSeries = generateValuesSPX(
            fromYear,
            toYear,
            annualReturns
          );
          setSecondAssetData((p) => ({
            ...p,
            secondAssetValues: annualReturns,
            secondAssetReturns: annualReturnSeries,
          }));
          return;
        }

        if (usePyScorecard) return;
        await callAPI(
          {
            fromYear: 1901,
            toYear: 2025,
            type: apiType,
            frequency: timeFrame,
            subType: "pctret",
          },
          (res) =>
            setSecondAssetData((p) => ({ ...p, secondAssetValues: res })),
          (res) =>
            setSecondAssetData((p) => ({ ...p, secondAssetReturns: res }))
        );
      }
    },
    [
      callAPI,
      timeFrame,
      fromYear,
      toYear,
      cpiValues,
      firstAssetValues,
      bins,
      resolveAssetSource,
      usePyScorecard,
    ]
  );

  const handleLock = useCallback(() => {
    if (usePyScorecard && lock) setUnlockNonce((n) => n + 1);
    setLock(!lock);
  }, [lock, usePyScorecard]);

  // Compute cartoons
  const computeCartoons = useCallback(
    (value) => {
      const cartoonCases = {
        "Store-of-Value [CPI+2%]": ["Store-of-Value CPI", [2, 2, 2, 2]],
        "Store-of-Value [7%]": ["Store-of-Value", [7, 7, 7, 7]],
        "Alpha [20,7.5,5,5]": ["Alpha", [20, 7.5, 5, 5]],
        "Alpha [15,10,2.5,2.5]": ["Alpha", [15, 10, 2.5, 2.5]],
        "INSUR[10-to-1]": ["Insurance", [1000, -100, -100, -100]],
        "INSUR[12-to-1]": ["Insurance", [1200, -100, -100, -100]],
        "INSUR[15-to-1]": ["Insurance", [1500, -100, -100, -100]],
      };

      if (cartoonCases[value]) {
        const [type, values] = cartoonCases[value];
        setSecondAssetData((p) => ({
          ...p,
          secondAssetValues: [
            ...generateCartoonsReturns(
              fromYear,
              toYear,
              type,
              values,
              cpiValues,
              firstAssetValues,
              bins
            ),
          ],
        }));
      }
    },
    [fromYear, toYear, cpiValues, firstAssetValues, bins]
  );

  // Filter data
  const filterData = useCallback(
    (
      label,
      raw_data,
      from,
      to,
      calculateReturns = false,
      returnsSetterFxn = null,
      sort = true,
      multiplier = 100
    ) => {
      let response = {
        labels: [],
        datasets: [
          {
            label: label,
            data: [],
            backgroundColor: "rgba(255, 99, 132, 0.5)",
          },
        ],
      };

      const sortedData = [...raw_data].sort((a, b) => a[1] - b[1]);
      sortedData.forEach((element) => {
        if (element[0] >= from && element[0] <= to) {
          response.labels.push(element[0]);
          response.datasets[0].data.push(parseFloat(element[1] * multiplier));
        }
      });

      if (sort) {
        response.datasets[0].data.sort((a, b) => a - b);
      }

      if (calculateReturns) {
        let returns = generateValuesSPX(from, to, raw_data);
        returnsSetterFxn(returns);
      }

      return response;
    },
    []
  );

  // Calculate regime selectors
  const calculateRegimeSelectors = useCallback(
    (response) => {
      let dataLength = response.datasets[0].data.length;
      let stepSize = Math.round(dataLength / (regimeCount + 1));
      let buffer = [1];
      for (let i = 1; i <= regimeCount; i++) {
        buffer.push(stepSize * i);
      }
      buffer.push(dataLength);
      setRegimeSelectors(buffer);
    },
    [regimeCount]
  );

  // Update regime
  const updateRegime = useCallback(
    (value, index, justPlot = false) => {
      let cartoonBins = [];
      let rs = regimeSelectors;
      if (!justPlot) {
        let pos = index.split("_")[2];
        let buffer = [...regimeSelectors];
        buffer[pos] = value;
        setRegimeSelectors(buffer);
        rs = buffer;
      }
      let data = dataBar?.datasets[0]?.data;
      let dataLabels = dataBar?.labels;
      let yearRange = [];

      let newResponse = {
        labels: [],
        datasets: [
          {
            label: "Num of years",
            data: [],
            backgroundColor: "rgba(53, 162, 235, 0.5)",
          },
        ],
      };

      for (let i = 1; i < rs.length; i++) {
        const start = rs[i - 1] - 1;
        const end = rs[i] - 1;
        let holderArray = dataLabels.slice(start, end);
        if (i == rs.length - 1) {
          holderArray = dataLabels.slice(start, end + 1);
        }
        yearRange.push(holderArray);
        cartoonBins.push([parseFloat(data[start]), parseFloat(data[end])]);
        newResponse.labels.push(
          parseFloat(data[start]?.toFixed(2)) +
          " to " +
          parseFloat(data[end]?.toFixed(2))
        );
        newResponse.datasets[0].data.push({ y: holderArray.length });
      }
      setYearsList(yearRange);
      setRegimeBar(newResponse);
      let newResNoCrash = JSON.parse(JSON.stringify(newResponse));
      newResNoCrash.datasets[0].data[0]["y"] = 0;
      setRegimeBarForNocrash(newResNoCrash);
      setBins(cartoonBins);
    },
    [regimeSelectors, dataBar]
  );

  // Update tables
  const updateTable = useCallback(
    (
      percentileValuesForLastCol,
      selectedValueFlip,
      wealthMatrixData,
      profitLossData
    ) => {
      let cagrData = cagrCal(
        percentileValuesForLastCol,
        wealthMatrixData,
        selectedValueFlip
      );
      let amData = amDataCal(percentileValuesForLastCol, profitLossData);
      let amDataAvg = avgCalc(amData);
      let gmCal = cagrData[5];

      setAmValues((p) => ({ ...p, am: Number(amDataAvg).toFixed(1) }));
      setGmValues((p) => ({ ...p, gm: Number(gmCal).toFixed(1) }));
    },
    []
  );

  const updateTableForNocrash = useCallback(
    (
      percentileValuesForLastCol,
      selectedValueFlip,
      wealthMatrixData,
      profitLossData
    ) => {
      let cagrData = cagrCal(
        percentileValuesForLastCol,
        wealthMatrixData,
        selectedValueFlip
      );
      const matrixWithOnesColumn = wealthMatrixData.map((row) => [1, ...row]);
      let amData = amDataCal(percentileValuesForLastCol, profitLossData);
      let amDataAvg = avgCalc(amData);
      let gmCal = cagrData[5];
      setAmValues((p) => ({ ...p, amNocrash: amDataAvg }));
      setGmValues((p) => ({ ...p, gmNocrash: gmCal }));
    },
    []
  );

  // Generate dice matrices
  const generateDiceMatrixForOneTwentyFaceDice = useCallback(
    (yearsList) => {
      let initialSPXReturn = [...firstAssetValues].sort((a, b) => a[0] - b[0]);

      let minFace, maxFace;
      for (let i = 0; i < initialSPXReturn.length; i++) {
        if (initialSPXReturn[i][0] === fromYear) minFace = i;
        if (initialSPXReturn[i][0] === toYear) maxFace = i;
      }

      let profitLossVal = new Map();
      for (let j = minFace; j <= maxFace; j++) {
        profitLossVal.set(j + 1, initialSPXReturn[j][1]);
      }
      minFace = 1 + minFace;
      maxFace = 1 + maxFace;

      const matrix = [];
      for (let i = 0; i < selectedValuePath; i++) {
        const row = [];
        for (let j = 0; j < selectedValueFlip; j++) {
          const randomFace =
            Math.floor(scorecardRandom() * (maxFace - minFace + 1)) + minFace;
          row.push(randomFace);
        }
        matrix.push(row);
      }

      let yearMatrix = calYearMatrix(initialSPXReturn, matrix);
      const matrixData = calculateWealthMatrixForOneTwenty(
        matrix,
        profitLossVal
      );
      const wealthMatrixData = matrixData[0];
      const profitLossData = matrixData[1];
      setBaseAssetMatrixReturns(profitLossData);

      let percentileValuesForLastCol = calculatePercentilePaths(
        wealthMatrixData,
        selectedValueFlip,
        selectedValuePath
      );

      updateTable(
        percentileValuesForLastCol,
        selectedValueFlip,
        wealthMatrixData,
        profitLossData
      );

      const resultArray = new Array(yearsList.length).fill([]).map(() => []);
      for (let i = 0; i < yearMatrix.length; i++) {
        for (let j = 0; j < yearMatrix[i].length; j++) {
          yearsList.forEach((regime, index) => {
            if (regime.includes(yearMatrix[i][j])) {
              resultArray[index].push(yearMatrix[i][j]);
            }
          });
        }
      }

      let avgRegime = [],
        maxValRegime = [],
        minValRegime = [];
      let resultArraySpxReturns = calReturn(resultArray, initialSPXReturn);

      for (let i = 0; i < resultArraySpxReturns.length; i++) {
        avgRegime.push(avgCalculate(resultArraySpxReturns[i]));
        maxValRegime.push(maxCalc(resultArraySpxReturns[i]));
        minValRegime.push(minCalc(resultArraySpxReturns[i]));
      }

      setMinMaxAvgArray({
        minArray: minValRegime,
        maxArray: maxValRegime,
        avgArray: avgRegime,
      });
      setYearMatrixData(yearMatrix);
      return [yearMatrix, yearsList];
    },
    [
      firstAssetValues,
      fromYear,
      toYear,
      selectedValuePath,
      selectedValueFlip,
      updateTable,
    ]
  );

  const generateDiceMatrixForOneTwentyFaceDiceForNoCrash = useCallback(
    (yearsList) => {
      let initialSPXReturn = [...firstAssetValues].sort((a, b) => a[0] - b[0]);

      let minFace, maxFace;
      let facesExceptFirstRegime = [];

      for (let i = 0; i < initialSPXReturn.length; i++) {
        if (initialSPXReturn[i][0] === fromYear) minFace = i;
        if (initialSPXReturn[i][0] === toYear) maxFace = i;
      }

      let profitLossVal = new Map();
      for (let j = minFace; j <= maxFace; j++) {
        profitLossVal.set(j + 1, initialSPXReturn[j][1]);
        if (!yearsList[0].includes(initialSPXReturn[j][0])) {
          facesExceptFirstRegime.push(j + 1);
        }
      }

      const matrix = Array.from({ length: selectedValuePath }, () =>
        Array.from(
          { length: selectedValueFlip },
          () =>
            facesExceptFirstRegime[
            Math.floor(scorecardRandom() * facesExceptFirstRegime.length)
            ]
        )
      );

      let yearMatrix = calYearMatrix(initialSPXReturn, matrix);
      const matrixData = calculateWealthMatrixForOneTwenty(
        matrix,
        profitLossVal
      );
      const wealthMatrixData = matrixData[0];
      const profitLossData = matrixData[1];
      setBaseAssetMatrixReturnsNoCrash(profitLossData);

      let percentileValuesForLastCol = calculatePercentilePaths(
        wealthMatrixData,
        selectedValueFlip,
        selectedValuePath
      );
      updateTableForNocrash(
        percentileValuesForLastCol,
        selectedValueFlip,
        wealthMatrixData,
        profitLossData
      );

      const resultArray = new Array(yearsList.length).fill([]).map(() => []);
      for (let i = 0; i < yearMatrix.length; i++) {
        for (let j = 0; j < yearMatrix[i].length; j++) {
          yearsList.forEach((regime, index) => {
            if (regime.includes(yearMatrix[i][j])) {
              resultArray[index].push(yearMatrix[i][j]);
            }
          });
        }
      }

      let avgRegime = [0],
        maxValRegime = [0],
        minValRegime = [0];
      let resultArraySpxReturns = calReturn(resultArray, initialSPXReturn);

      for (let i = 1; i < resultArraySpxReturns.length; i++) {
        avgRegime.push(avgCalculate(resultArraySpxReturns[i]));
        maxValRegime.push(maxCalc(resultArraySpxReturns[i]));
        minValRegime.push(minCalc(resultArraySpxReturns[i]));
      }
      setMinMaxAvgArrayNoCrash({
        minArrayNocrash: minValRegime,
        maxArrayNocrash: maxValRegime,
        averageArrayNocrash: avgRegime,
      });
      setYearMatrixDataNocrash(yearMatrix);
      return [yearMatrix, yearsList];
    },
    [
      firstAssetValues,
      fromYear,
      toYear,
      selectedValuePath,
      selectedValueFlip,
      updateTableForNocrash,
    ]
  );

  // Calculate second asset functions
  const calSecondAsset = useCallback(
    (yearMatrix, yearsList) => {
      if (yearMatrix.length == 0 && yearMatrixData.length != 0) {
        yearMatrix = yearMatrixData;
      }
      let binMatrix = createBinMatrix(yearMatrix, yearsList);
      let assetBin = filterRegimeYears(yearsList, secondAssetValues);
      let newAssetYearMatrix = fillNewMatrix(binMatrix, assetBin);

      const resultAssetArray = new Array(assetBin.length)
        .fill([])
        .map(() => []);
      for (let i = 0; i < newAssetYearMatrix.length; i++) {
        for (let j = 0; j < newAssetYearMatrix[i].length; j++) {
          assetBin.forEach((regime, index) => {
            if (regime.includes(newAssetYearMatrix[i][j])) {
              resultAssetArray[index].push(newAssetYearMatrix[i][j]);
            }
          });
        }
      }

      let avgAssetRegime = [],
        maxAssetValRegime = [],
        minAssetValRegime = [];
      let resultArrayReturns = calReturn(resultAssetArray, secondAssetValues);

      for (let i = 0; i < resultArrayReturns.length; i++) {
        avgAssetRegime.push(avgCalculate(resultArrayReturns[i]));
        maxAssetValRegime.push(maxCalc(resultArrayReturns[i]));
        minAssetValRegime.push(minCalc(resultArrayReturns[i]));
      }

      setMinMaxAvgAssetArray({
        minAssetArray: minAssetValRegime,
        maxAssetArray: maxAssetValRegime,
        averageAssetArray: avgAssetRegime,
      });

      let pnlValues = generatePNLValues(secondAssetValues);
      const matrixData = calculateWealthMatrixForOneTwenty(
        newAssetYearMatrix,
        pnlValues,
        "secondAsset"
      );
      let wealthMatrixData = matrixData[0];
      let profitLossData = matrixData[1];
      setOtherAssetMatrixReturns(profitLossData);

      let percentileValuesForLastCol = calculatePercentilePaths(
        wealthMatrixData,
        selectedValueFlip,
        selectedValuePath
      );

      if (
        percentileValuesForLastCol?.length > 1 &&
        wealthMatrixData.length > 1 &&
        profitLossData.length > 1
      ) {
        const AmGm = calAmGm(
          percentileValuesForLastCol,
          selectedValueFlip,
          wealthMatrixData,
          profitLossData
        );
        setAmValues((p) => ({ ...p, amMean: AmGm.am }));
      }
    },
    [yearMatrixData, secondAssetValues, selectedValueFlip, selectedValuePath]
  );

  const calSecondAssetForNocrash = useCallback(
    (yearMatrix, yearsList) => {
      if (yearMatrix.length == 0 && yearMatrixData.length != 0) {
        yearMatrix = yearMatrixData;
      }
      let binMatrix = createBinMatrix(yearMatrix, yearsList);
      let assetBin = filterRegimeYears(yearsList, secondAssetValues);
      let newAssetYearMatrix = fillNewMatrix(binMatrix, assetBin);

      const resultAssetArray = new Array(assetBin.length)
        .fill([])
        .map(() => []);
      for (let i = 0; i < newAssetYearMatrix.length; i++) {
        for (let j = 0; j < newAssetYearMatrix[i].length; j++) {
          assetBin.forEach((regime, index) => {
            if (regime.includes(newAssetYearMatrix[i][j])) {
              resultAssetArray[index].push(newAssetYearMatrix[i][j]);
            }
          });
        }
      }

      let avgAssetRegime = [0],
        maxAssetValRegime = [0],
        minAssetValRegime = [0];
      let resultArrayReturns = calReturn(resultAssetArray, secondAssetValues);

      for (let i = 1; i < resultArrayReturns.length; i++) {
        avgAssetRegime.push(avgCalculate(resultArrayReturns[i]));
        maxAssetValRegime.push(maxCalc(resultArrayReturns[i]));
        minAssetValRegime.push(minCalc(resultArrayReturns[i]));
      }

      setMinMaxAvgAssetArrayNocrash({
        minAssetArrayNocrash: minAssetValRegime,
        maxAssetArrayNocrash: maxAssetValRegime,
        averageAssetArrayNocrash: avgAssetRegime,
      });

      let pnlValues = generatePNLValues(secondAssetValues);
      const matrixData = calculateWealthMatrixForOneTwenty(
        newAssetYearMatrix,
        pnlValues,
        "secondAsset"
      );
      let wealthMatrixData = matrixData[0];
      let profitLossData = matrixData[1];
      setOtherAssetMatrixReturnsNoCrash(profitLossData);

      let percentileValuesForLastCol = calculatePercentilePaths(
        wealthMatrixData,
        selectedValueFlip,
        selectedValuePath
      );

      if (
        percentileValuesForLastCol?.length > 1 &&
        wealthMatrixData.length > 1 &&
        profitLossData.length > 1
      ) {
        const AmGm = calAmGm(
          percentileValuesForLastCol,
          selectedValueFlip,
          wealthMatrixData,
          profitLossData
        );
        setAmValues((p) => ({ ...p, amMeanNocrash: AmGm.am }));
      }
    },
    [yearMatrixData, secondAssetValues, selectedValueFlip, selectedValuePath]
  );

  const calSecondAssetForReshuffle = useCallback(
    (yearMatrix, yearsList) => {
      if (yearMatrix.length == 0 && yearMatrixData.length != 0) {
        yearMatrix = yearMatrixData;
      }
      let binMatrix = createBinMatrix(yearMatrix, yearsList);
      let assetBin = filterRegimeYears(yearsList, secondAssetValues);
      let newAssetYearMatrix = fillNewMatrix(binMatrix, assetBin);
      const shuffledMatrix = shuffleMatrixRows(newAssetYearMatrix);

      const resultAssetArray = calReshuffledBin(
        shuffledMatrix,
        binMatrix,
        assetBin
      );

      let avgAssetRegime = [],
        maxAssetValRegime = [],
        minAssetValRegime = [];
      let resultArrayReturns = calReturn(resultAssetArray, secondAssetValues);

      for (let i = 0; i < resultArrayReturns.length; i++) {
        avgAssetRegime.push(avgCalculate(resultArrayReturns[i]));
        maxAssetValRegime.push(maxCalc(resultArrayReturns[i]));
        minAssetValRegime.push(minCalc(resultArrayReturns[i]));
      }

      setMinMaxAvgAssetReshuffleArray({
        minAssetReshuffleArray: minAssetValRegime,
        maxAssetReshuffleArray: maxAssetValRegime,
        averageAssetReshuffleArray: avgAssetRegime,
      });

      let pnlValues = generatePNLValues(secondAssetValues);
      const matrixData = calculateWealthMatrixForOneTwenty(
        shuffledMatrix,
        pnlValues,
        "secondAssetReshuffle"
      );
      let wealthMatrixData = matrixData[0];
      let profitLossData = matrixData[1];
      setOtherAssetMatrixReshuffleReturns(profitLossData);

      let percentileValuesForLastCol = calculatePercentilePaths(
        wealthMatrixData,
        selectedValueFlip,
        selectedValuePath
      );

      if (
        percentileValuesForLastCol?.length > 1 &&
        wealthMatrixData.length > 1 &&
        profitLossData.length > 1
      ) {
        const AmGm = calAmGm(
          percentileValuesForLastCol,
          selectedValueFlip,
          wealthMatrixData,
          profitLossData
        );
        setAmValues((p) => ({ ...p, amMeanReshuffle: AmGm.am }));
      }
    },
    [yearMatrixData, secondAssetValues, selectedValueFlip, selectedValuePath]
  );

  // Mix calculations
  const calAssetsMixReshuffle = useCallback(
    (yearMatrix, yearsList) => {
      if (
        baseAssetMatrixRetruns.length > 1 &&
        otherAssetMatrixReshuffleReturns.length > 1
      ) {
        const mixMatrix = generateMix(
          baseAssetMatrixRetruns,
          otherAssetMatrixReshuffleReturns,
          alloc["secondAssetAlloc"]
        );
        let binMatrix = createBinMatrix(yearMatrixData, yearsList);
        let mappedBin = binMapping(binMatrix, mixMatrix);

        let avgAssetRegime = [],
          maxAssetValRegime = [],
          minAssetValRegime = [];

        for (let bin in mappedBin) {
          avgAssetRegime.push(avgCalculate(mappedBin[bin]));
          maxAssetValRegime.push(maxCalc(mappedBin[bin]));
          minAssetValRegime.push(minCalc(mappedBin[bin]));
        }

        setMixAssetReshuffleArray({
          mixMinAssetReshuffleArray: minAssetValRegime,
          mixMaxAssetReshuffleArray: maxAssetValRegime,
          mixAvgAssetReshuffleArray: avgAssetRegime,
        });

        const wealthMatrix = generateMixReturnsWealth(mixMatrix);
        let percentileValuesForLastCol = calculatePercentilePaths(
          wealthMatrix,
          selectedValueFlip,
          selectedValuePath
        );
        const AmGm = calAmGm(
          percentileValuesForLastCol,
          selectedValueFlip,
          wealthMatrix,
          mixMatrix
        );
        setMixAmGmReshuffle({
          mixAMReshuffle: AmGm.am,
          mixGMReshuffle: AmGm.gm,
        });
      }
    },
    [
      baseAssetMatrixRetruns,
      otherAssetMatrixReshuffleReturns,
      alloc,
      yearMatrixData,
      selectedValueFlip,
      selectedValuePath,
    ]
  );

  const calAssetMixNoCrash = useCallback(() => {
    if (
      baseAssetMatrixReturnsNoCrash.length > 1 &&
      otherAssetMatrixReturnsNoCrash.length > 1
    ) {
      const mixMatrix = generateMix(
        baseAssetMatrixReturnsNoCrash,
        otherAssetMatrixReturnsNoCrash,
        alloc["secondAssetAlloc"]
      );
      let binMatrix = createBinMatrix(yearMatrixDataNocrash, yearsList);
      let mappedBin = binMapping(binMatrix, mixMatrix);

      let avgAssetRegime = [0],
        maxAssetValRegime = [0],
        minAssetValRegime = [0];

      for (let bin in mappedBin) {
        avgAssetRegime.push(avgCalculate(mappedBin[bin]));
        maxAssetValRegime.push(maxCalc(mappedBin[bin]));
        minAssetValRegime.push(minCalc(mappedBin[bin]));
      }

      setMinMaxAvgMixNoCrash({
        minMixNoCrash: minAssetValRegime,
        maxMixNoCrash: maxAssetValRegime,
        avgMixNoCrash: avgAssetRegime,
      });

      const wealthMatrix = generateMixReturnsWealth(mixMatrix);
      let percentileValuesForLastCol = calculatePercentilePaths(
        wealthMatrix,
        selectedValueFlip,
        selectedValuePath
      );
      const AmGm = calAmGm(
        percentileValuesForLastCol,
        selectedValueFlip,
        wealthMatrix,
        mixMatrix
      );
      setAmGmMixNoCrash({
        amMixNoCrash: AmGm.am,
        gmMixNoCrash: AmGm.gm,
      });
    }
  }, [
    baseAssetMatrixReturnsNoCrash,
    otherAssetMatrixReturnsNoCrash,
    alloc,
    yearMatrixDataNocrash,
    yearsList,
    selectedValueFlip,
    selectedValuePath,
  ]);

  // Event handlers
  const handleYearChange = useCallback(({ min, max }) => {
    const from = parseInt(min);
    const to = parseInt(max);
    setFromYear((prevFrom) => {
      setToYear((prevTo) => {
        if (from === prevFrom && to === prevTo) return prevTo;
        setRegimeSelectors(
          buildDefaultRegimeSelectors(to - from + 1, regimeCount)
        );
        return to;
      });
      return from;
    });
  }, [buildDefaultRegimeSelectors, regimeCount]);

  const handleRegimeCommit = useCallback(() => {
    if (usePyScorecard) {
      setRegimeCommitNonce((n) => n + 1);
    }
  }, [usePyScorecard]);

  const handleAllocCommit = useCallback(() => {
    if (!usePyScorecard) return;
    setAllocCommitNonce((n) => n + 1);
  }, [usePyScorecard]);

  const handleDefault = useCallback(() => {
    setSelectedType("normal");
  }, []);

  const handleReshuffle = useCallback(() => {
    setSelectedType("reshuffle");
    if (usePyScorecard) return; // mode data already in chart5ByMode / mix*Reshuffle
    calAssetsMixReshuffle(yearMatrixData, yearsList);
  }, [calAssetsMixReshuffle, yearMatrixData, yearsList, usePyScorecard]);

  const handleNocrash = useCallback(() => {
    setSelectedType("nocrash");
    if (usePyScorecard) return;
    calAssetMixNoCrash();
  }, [calAssetMixNoCrash, usePyScorecard]);

  const handleAllocSliderChange = useCallback((e) => {
    setAlloc({
      firstAssetAlloc: 100 - e.target.value,
      secondAssetAlloc: e.target.value,
    });
  }, []);

  const handlePercentileDropdown = useCallback((event, id) => {
    if (id === "percentile_1") {
      setPercentileDropdown((p) => ({
        ...p,
        percentile1Dropdown: event,
      }));
    } else if (id === "percentile_2") {
      setPercentileDropdown((p) => ({
        ...p,
        percentile2Dropdown: event,
      }));
    }
  }, []);

  const handleInsuranceSliderValue = useCallback((e) => {
    const raw = e.target.value;
    setInsuranceSliderValue(raw);
    let newValue = parseInt(raw);
    if (INSURANCE_TYPES.some((type) => secondAsset.includes(type))) {
      if (newValue <= 60) {
        newValue = (0.1 + (newValue / 60) * (10.0 - 0.1)).toFixed(1);
      } else if (newValue >= 61 && newValue <= 100) {
        newValue = Math.round(
          11 + ((newValue - 61) / (100 - 61)) * (100 - 11)
        ).toFixed(0);
      }
      setAlloc({
        firstAssetAlloc: 100 - newValue,
        secondAssetAlloc: newValue,
      });
    }
  }, [secondAsset]);

  const handleSliderDisplayValue = useCallback(() => {
    // kept for compatibility; insurance alloc now updates in handleInsuranceSliderValue
  }, []);

  // Phase A: Python Safe Haven Scorecard bootstrap (feature-flagged)
  useEffect(() => {
    if (!usePyScorecard) return undefined;

    const cartoon = resolveCartoonConfig(secondAsset);
    if (!firstAsset) return undefined;
    if (!cartoon && !secondAsset) return undefined;

    let cancelled = false;
    setLoading(true);

    const isUserAllocCommit =
      allocCommitNonce !== lastAllocCommitNonceRef.current;
    lastAllocCommitNonceRef.current = allocCommitNonce;
    const useRecommendedAlloc = !lockRef.current && !isUserAllocCommit;

    const run = async () => {
      try {
        const currentAlloc = allocRef.current;
        const selectorsValid =
          Array.isArray(regimeSelectors) &&
          regimeSelectors.length === regimeCount + 2;

        const payload = buildScorecardRequest({
          baseAsset: firstAsset,
          otherAsset: secondAsset,
          fromYear,
          toYear,
          regimeCount,
          regimeSelectors: selectorsValid ? regimeSelectors : undefined,
          pathCount: selectedValuePath,
          horizon: selectedValueFlip,
          firstAssetAlloc: currentAlloc.firstAssetAlloc,
          secondAssetAlloc: currentAlloc.secondAssetAlloc,
          useRecommendedAlloc,
          percentile1: percentileDropdown.percentile1Dropdown,
          percentile2: percentileDropdown.percentile2Dropdown,
          cartoon,
          seed: PARITY_SEED,
          userId: scorecardUserId,
          mode: selectedType,
        });
        dispatch(portfolioRequestStart(payload));
        const result = await callSafeHavenScorecard(payload);
        if (cancelled) return;
        dispatch(
          portfolioRequestSuccess({ response: result, requestPayload: payload })
        );
        const mixAlloc = result?.modes?.[selectedType]?.mix?.alloc;
        if (useRecommendedAlloc && mixAlloc != null) {
          const second = Number(mixAlloc);
          setAlloc({ firstAssetAlloc: 100 - second, secondAssetAlloc: second });
        }
        applyScorecardResponse(
          result,
          {
            setDataBar,
            setRegimeBar,
            setRegimeBarForNocrash,
            setYearsList,
            setBins,
            setRegimeSelectors,
            setMinMaxAvgArray,
            setMinMaxAvgArrayNoCrash,
            setMinMaxAvgAssetArray,
            setMinMaxAvgAssetArrayNocrash,
            setMinMaxAvgAssetReshuffleArray,
            setMixAssetArray,
            setMixAssetReshuffleArray,
            setMinMaxAvgMixNoCrash,
            setAmValues,
            setGmValues,
            setMixAmGm,
            setMixAmGmReshuffle,
            setAmGmMixNoCrash,
            setPercentile2DataMaxAllocation,
            setChart5ByMode,
            setLoading,
          },
          { mode: selectedType }
        );
      } catch (error) {
        console.error("safeHavenScorecard API failed", error);
        if (!cancelled) {
          dispatch(portfolioRequestFailure(error));
          setLoading(false);
        }
      }
    };

    const timer = setTimeout(run, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    firstAsset,
    secondAsset,
    fromYear,
    toYear,
    regimeCount,
    regimeCommitNonce,
    allocCommitNonce,
    unlockNonce,
    percentileDropdown.percentile1Dropdown,
    percentileDropdown.percentile2Dropdown,
    selectedType,
    scorecardUserId,
    selectedValuePath,
    selectedValueFlip,
    usePyScorecard,
    dispatch,
  ]);

  // JS Monte Carlo needs browser series; Python API fetches server-side — skip here.
  useEffect(() => {
    if (usePyScorecard) return undefined;

    callAPI(
      { fromYear: 1901, toYear: 2025, type: firstAsset, frequency: timeFrame },
      (res) => setFirstAssetData((p) => ({ ...p, firstAssetValues: res })),
      (res) => setFirstAssetData((p) => ({ ...p, firstAssetReturns: res }))
    );
    callAPI(
      {
        fromYear: 1901,
        toYear: 2025,
        type: secondAsset,
        frequency: timeFrame,
        subType: "pctret",
      },
      (res) => setSecondAssetData((p) => ({ ...p, secondAssetValues: res })),
      (res) => setSecondAssetData((p) => ({ ...p, secondAssetReturns: res }))
    );
    callAPI(
      { fromYear: 1901, toYear: 2025, type: "cpi", frequency: "a" },
      setCpiValues
    );
  }, [usePyScorecard]); // SCORECARD_PARITY_TESTKIT — re-fetch when toggling py/js

  useEffect(() => {
    if (usePyScorecard) return;
    if (!lock) {
      setAlloc((prev) => {
        const second = percentile2DataMaxReturnAllocation;
        const first = 100 - second;
        if (
          prev.firstAssetAlloc === first &&
          prev.secondAssetAlloc === second
        ) {
          return prev;
        }
        return { firstAssetAlloc: first, secondAssetAlloc: second };
      });
    }
  }, [percentile2DataMaxReturnAllocation, lock, usePyScorecard]);

  useEffect(() => {
    if (usePyScorecard) return;
    let buffer = filterData(
      firstAssetName,
      firstAssetValues,
      fromYear,
      toYear,
      true,
      (res) => setFirstAssetData((p) => ({ ...p, firstAssetReturns: res })),
      true
    );
    calculateRegimeSelectors(buffer);
    if (fromYear == 1901 && toYear == 2020 && regimeCount == 4) {
      setRegimeSelectors([1, 11, 33, 62, 98, 120]);
    }
    setDataBar(buffer);
  }, [
    fromYear,
    toYear,
    firstAssetValues,
    regimeCount,
    filterData,
    calculateRegimeSelectors,
    firstAssetName,
  ]);

  useEffect(() => {
    if (usePyScorecard) return;
    updateRegime(null, null, true);
  }, [dataBar]);

  useEffect(() => {
    if (usePyScorecard) return;
    if (
      (selectedType === "normal" ||
        selectedType === "reshuffle" ||
        selectedType === "nocrash") &&
      loading === false &&
      firstAssetValues.length > 0 &&
      secondAssetValues.length > 0 &&
      yearsList?.length > 0
    ) {
      resetParityRng(PARITY_SEED);
      const [yearMatrix] =
        generateDiceMatrixForOneTwentyFaceDice(yearsList) || [];
      const [yearMatrixNocrash] =
        generateDiceMatrixForOneTwentyFaceDiceForNoCrash(yearsList) || [];
      if (yearMatrix?.length > 0) {
        calSecondAsset(yearMatrix, yearsList);
        calSecondAssetForReshuffle(yearMatrix, yearsList);
      }
      if (yearMatrixNocrash?.length > 0) {
        calSecondAssetForNocrash(yearMatrixNocrash, yearsList);
      }
    }
  }, [
    fromYear,
    toYear,
    regimeCount,
    regimeSelectors,
    yearsList,
    loading,
    selectedType,
    firstAssetValues,
    secondAssetValues,
    usePyScorecard,
  ]);

  useEffect(() => {
    if (usePyScorecard) return;
    computeCartoons(secondAssetName);
  }, [
    fromYear,
    toYear,
    regimeCount,
    regimeSelectors,
    bins,
    secondAssetName,
    computeCartoons,
  ]);

  useEffect(() => {
    if (usePyScorecard) return;
    if (selectedType === "reshuffle") {
      calAssetsMixReshuffle(yearMatrixData, yearsList);
    }
  }, [
    firstAssetValues,
    secondAssetValues,
    fromYear,
    toYear,
    alloc,
    yearsList,
    yearMatrixData,
    regimeCount,
    regimeSelectors,
    baseAssetMatrixRetruns,
    otherAssetMatrixReshuffleReturns,
    selectedType,
  ]);

  useEffect(() => {
    if (usePyScorecard) return;
    if (selectedType === "nocrash") {
      calAssetMixNoCrash();
    }
  }, [
    firstAssetValues,
    secondAssetValues,
    secondAssetName,
    fromYear,
    toYear,
    alloc,
    yearsList,
    yearMatrixDataNocrash,
    regimeCount,
    regimeSelectors,
    baseAssetMatrixReturnsNoCrash,
    otherAssetMatrixReturnsNoCrash,
    selectedType,
    calAssetMixNoCrash,
  ]);

  useEffect(() => {
    if (usePyScorecard) return;
    if (
      baseAssetMatrixRetruns.length > 1 &&
      otherAssetMatrixRetruns.length > 1
    ) {
      const mixMatrix = generateMix(
        baseAssetMatrixRetruns,
        otherAssetMatrixRetruns,
        alloc["secondAssetAlloc"]
      );

      let binMatrix = createBinMatrix(yearMatrixData, yearsList);
      let mappedBin = binMapping(binMatrix, mixMatrix);

      let avgAssetRegime = [],
        maxAssetValRegime = [],
        minAssetValRegime = [];

      for (let bin in mappedBin) {
        avgAssetRegime.push(avgCalculate(mappedBin[bin]));
        maxAssetValRegime.push(maxCalc(mappedBin[bin]));
        minAssetValRegime.push(minCalc(mappedBin[bin]));
      }

      setMixAssetArray({
        mixMinAssetArray: minAssetValRegime,
        mixMaxAssetArray: maxAssetValRegime,
        mixAvgAssetArray: avgAssetRegime,
      });

      const wealthMatrix = generateMixReturnsWealth(mixMatrix);
      let percentileValuesForLastCol = calculatePercentilePaths(
        wealthMatrix,
        selectedValueFlip,
        selectedValuePath
      );
      const AmGm = calAmGm(
        percentileValuesForLastCol,
        selectedValueFlip,
        wealthMatrix,
        mixMatrix
      );
      setMixAmGm({
        mixAM: Number(AmGm.am).toFixed(1),
        mixGM: Number(AmGm.gm).toFixed(1),
      });
    }
  }, [
    baseAssetMatrixRetruns,
    otherAssetMatrixRetruns,
    alloc,
    minMaxAvgArray?.avgArray,
    yearMatrixData,
    yearsList,
    selectedValueFlip,
    selectedValuePath,
  ]);

  const skipRegimeCountRebuildRef = useRef(true);
  useEffect(() => {
    if (!usePyScorecard) return;
    if (skipRegimeCountRebuildRef.current) {
      skipRegimeCountRebuildRef.current = false;
      return;
    }
    const dataLength = toYear - fromYear + 1;
    setRegimeSelectors(buildDefaultRegimeSelectors(dataLength, regimeCount));
  }, [regimeCount, usePyScorecard, buildDefaultRegimeSelectors]);

  // Memoized values
  const showInsuranceSlider = useMemo(() => {
    return INSURANCE_TYPES.some((type) => secondAsset.includes(type));
  }, [secondAsset]);

  return {
    // State
    loading,
    selectedType,
    fromYear,
    toYear,
    firstAsset,
    firstAssetName,
    firstAssetValues,
    secondAsset,
    secondAssetName,
    secondAssetValues,
    firstAssetDropdown,
    secondAssetDropdown,
    regimeCount,
    regimeSelectors,
    dataBar,
    regimeBar,
    regimeBarForNocrash,
    yearMatrixData,
    yearsList,
    alloc,
    lock,
    insuranceSliderValue,
    percentileDropdown,
    isInsurance,
    showInsuranceSlider,
    allocCEAplotRef,

    // Arrays for charts
    minMaxAvgArray,
    minMaxAvgArrayNoCrash,
    minMaxAvgAssetArray,
    minMaxAvgAssetArrayNocrash,
    minMaxAvgAssetReshuffleArray,
    mixAssetArray,
    mixAssetReshuffleArray,
    minMaxAvgMixNoCrash,
    // Metrics
    amValues,
    gmValues,
    mixAmGm,
    mixAmGmReshuffle,
    amGmMixNoCrash,

    // Matrix data for Chart5
    baseAssetMatrixRetruns,
    otherAssetMatrixRetruns,
    otherAssetMatrixReshuffleReturns,
    baseAssetMatrixReturnsNoCrash,
    otherAssetMatrixReturnsNoCrash,
    chart5ByMode,
    usePyScorecard: usePyScorecard,

    // Handlers
    handleYearChange,
    handleFirstAsset,
    handleSecondAsset,
    handleDefault,
    handleReshuffle,
    handleNocrash,
    handleAllocSliderChange,
    handleAllocCommit,
    handleLock,
    handleInsuranceSliderValue,
    handlePercentileDropdown,
    updateRegime,
    handleRegimeCommit,
    setRegimeCount,
    setPercentile2DataMaxAllocation,
  };
};

export default useScorecard;
